// Teacher → Messages. Conversation list + thread, plus a composer to start a
// new 1:1 with a student or broadcast to a class. docs/replan-v2.md R10.

import { useT } from '@fajrarabi/i18n/react';
import { Alert, Button, Card, CardBody, Field } from '@fajrarabi/ui';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { ConversationList } from '~/components/messaging/ConversationList';
import { MessageThread } from '~/components/messaging/MessageThread';
import { ApiError } from '~/lib/api';
import {
  broadcastToClass,
  getMessages,
  listConversations,
  markConversationRead,
  replyToConversation,
  sendDirectMessage,
} from '~/lib/messaging';
import { listClassStudents, listTeacherClasses } from '~/lib/school';

export const Route = createFileRoute('/$locale/teacher/messages')({
  component: TeacherMessagesScreen,
});

function TeacherMessagesScreen() {
  const t = useT();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mode, setMode] = useState<'direct' | 'broadcast'>('direct');
  const [studentId, setStudentId] = useState('');
  const [classId, setClassId] = useState('');
  const [draft, setDraft] = useState('');

  const conversations = useQuery({
    queryKey: ['messages', 'conversations'],
    queryFn: ({ signal }) => listConversations(signal),
    refetchInterval: 15_000,
  });

  const classes = useQuery({
    queryKey: ['teacher', 'classes'],
    queryFn: ({ signal }) => listTeacherClasses(signal),
  });

  // Build the student picker from all of the teacher's classes.
  const studentQueries = useQueries({
    queries: (classes.data ?? []).map((cls) => ({
      queryKey: ['teacher', 'class', cls.id, 'students'],
      queryFn: ({ signal }: { signal: AbortSignal }) => listClassStudents(cls.id, signal),
    })),
  });
  const students = useMemo(() => {
    const byId = new Map<string, { id: string; name: string }>();
    for (const q of studentQueries) {
      for (const s of q.data ?? []) {
        byId.set(s.id, { id: s.id, name: s.displayName ?? s.username });
      }
    }
    return [...byId.values()];
  }, [studentQueries]);

  const messages = useQuery({
    queryKey: ['messages', 'thread', selectedId],
    queryFn: ({ signal }) => getMessages(selectedId!, signal),
    enabled: selectedId !== null,
    refetchInterval: selectedId ? 10_000 : false,
  });

  function selectConversation(id: string) {
    setSelectedId(id);
    void markConversationRead(id).then(() =>
      qc.invalidateQueries({ queryKey: ['messages', 'conversations'] }),
    );
  }

  const startDirect = useMutation({
    mutationFn: () => sendDirectMessage(studentId, draft.trim()),
    onSuccess: async (res) => {
      setDraft('');
      setSelectedId(res.conversationId);
      await qc.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  const broadcast = useMutation({
    mutationFn: () => broadcastToClass(classId, draft.trim()),
    onSuccess: async (res) => {
      setDraft('');
      setSelectedId(res.conversationId);
      await qc.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  const reply = useMutation({
    mutationFn: (body: string) => replyToConversation(selectedId!, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['messages', 'thread', selectedId] });
      await qc.invalidateQueries({ queryKey: ['messages', 'conversations'] });
    },
  });

  const selected = conversations.data?.find((c) => c.id === selectedId) ?? null;
  const canReplyToSelected = selected ? selected.kind === '1to1' && !selected.observer : false;
  const composeReady =
    mode === 'direct'
      ? studentId !== '' && draft.trim() !== ''
      : classId !== '' && draft.trim() !== '';

  return (
    <div className="mx-auto max-w-6xl px-6 py-10">
      <h1 className="text-2xl font-bold text-ink-900">{t('app.messages.title')}</h1>

      <Card className="mt-6">
        <CardBody>
          <div className="mb-4 flex gap-2">
            <ModeTab active={mode === 'direct'} onClick={() => setMode('direct')}>
              {t('app.messages.mode_direct')}
            </ModeTab>
            <ModeTab active={mode === 'broadcast'} onClick={() => setMode('broadcast')}>
              {t('app.messages.mode_broadcast')}
            </ModeTab>
          </div>
          {(startDirect.isError || broadcast.isError) && (
            <Alert tone="danger" className="mb-4">
              {(() => {
                const err = startDirect.error ?? broadcast.error;
                return err instanceof ApiError ? err.message : t('app.common.error_generic');
              })()}
            </Alert>
          )}
          <form
            className="grid items-end gap-4 sm:grid-cols-[1fr_auto]"
            onSubmit={(e) => {
              e.preventDefault();
              if (!composeReady) return;
              if (mode === 'direct') startDirect.mutate();
              else broadcast.mutate();
            }}
          >
            {mode === 'direct' ? (
              <Field label={t('app.messages.pick_student')}>
                {(id) => (
                  <select
                    id={id}
                    value={studentId}
                    onChange={(e) => setStudentId(e.target.value)}
                    className="h-11 w-full rounded-xl border border-ink-500/20 bg-white px-3 text-ink-900"
                  >
                    <option value="">—</option>
                    {students.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            ) : (
              <Field label={t('app.messages.pick_class')}>
                {(id) => (
                  <select
                    id={id}
                    value={classId}
                    onChange={(e) => setClassId(e.target.value)}
                    className="h-11 w-full rounded-xl border border-ink-500/20 bg-white px-3 text-ink-900"
                  >
                    <option value="">—</option>
                    {(classes.data ?? []).map((cls) => (
                      <option key={cls.id} value={cls.id}>
                        {cls.name}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            )}
            <div className="sm:col-span-2">
              <Field label={t('app.messages.message_label')}>
                {(id) => (
                  <textarea
                    id={id}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={2}
                    className="w-full rounded-xl border border-ink-500/20 bg-white px-3 py-2 text-ink-900"
                    placeholder={t('app.messages.compose_placeholder')}
                  />
                )}
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Button
                type="submit"
                disabled={!composeReady || startDirect.isPending || broadcast.isPending}
              >
                {mode === 'direct' ? t('app.messages.send') : t('app.messages.send_broadcast')}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <div className="mt-8 grid gap-6 md:grid-cols-[20rem_1fr]">
        <div className="rounded-2xl bg-white p-2 ring-1 ring-burgundy-500/10">
          <ConversationList
            conversations={conversations.data ?? []}
            loading={conversations.isLoading}
            selectedId={selectedId}
            onSelect={selectConversation}
          />
        </div>
        <div className="min-h-[24rem] rounded-2xl bg-white p-4 ring-1 ring-burgundy-500/10">
          {selectedId ? (
            <MessageThread
              messages={messages.data ?? []}
              loading={messages.isLoading}
              canReply={canReplyToSelected}
              sending={reply.isPending}
              error={reply.error}
              onSend={(body) => reply.mutate(body)}
            />
          ) : (
            <p className="grid h-full place-items-center text-sm text-ink-500">
              {t('app.messages.pick_conversation')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ModeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
        active ? 'bg-burgundy-500 text-cream-50' : 'bg-cream-200 text-ink-700 hover:bg-cream-200/70'
      }`}
    >
      {children}
    </button>
  );
}
